import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

import { ConnectorConfiguration, MicrosoftServiceType } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import {
  ConnectorForm,
  MicrosoftTeamsConfiguration,
} from './microsoft-teams-connector-dialog.models';

@Component({
  selector: 'blaze-microsoft-teams-connector-dialog',
  templateUrl: './microsoft-teams-connector-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogModule,
    AsyncPipe,
    EnvironmentIndicatorComponent,
    CopyConnectorConfigEnvButtonComponent,
    ButtonBarComponent,
    NgTemplateOutlet,
    ButtonComponent,
    ReactiveFormsModule,
    PanelComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class MicrosoftTeamsConnectorDialogComponent
  extends BaseConnectorDialogComponent<MicrosoftTeamsConfiguration>
  implements OnInit
{
  private panelRef = inject(PanelRef);

  serviceName = 'microsoft';
  connectorForm: FormGroup<ConnectorForm>;

  constructor() {
    super();

    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    });

    this.withOAuth();
  }

  ngOnInit(): void {
    if (!this.connector) {
      this.goToAuth();
    }

    const configuration = this.getConfiguration(this.connector);
    this.fillForm(configuration);

    if (this.authState) {
      this.goToForm();
    } else {
      this.goToAuth();
    }
  }

  getConfiguration(connector?: ConnectorConfiguration | undefined): MicrosoftTeamsConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const authState = String(connector?.configuration?.authState ?? '');

    return { connectorId, connectorName, authState };
  }

  fillForm(configuration: MicrosoftTeamsConfiguration): void {
    this.connectorId = configuration.connectorId;
    this.authState = configuration.authState;

    this.connectorForm.patchValue(
      {
        connectorName: configuration.connectorName,
      },
      { emitEvent: false },
    );
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName } = this.connectorForm.value;

    if (!connectorName) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        authState: this.authState,
      },
    );

    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
      });
  }

  protected override getAuthorizationUrl(): Observable<{ url: string }> {
    return this.connectorsApiService.getServiceAuthorizeUrl(this.serviceName, {
      serviceType: MicrosoftServiceType.Messaging,
    });
  }
}
